import React, { useState, useEffect } from 'react';
import {
  Modal, Steps, Form, Input, Select, DatePicker, TimePicker, Button,
  Card, Tag, List, Avatar, message, Row, Col, Divider, Alert,
} from 'antd';
import {
  UserOutlined, PlusOutlined, DeleteOutlined, CheckCircleOutlined, UsergroupAddOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import QRCode from 'react-qr-code';
import { educationAssetService, educationSessionService, userService, trainingService } from '@iso/services';
import AssessmentFormSelect from './AssessmentFormSelect';
import LiveAttendeeRoster from './LiveAttendeeRoster';

const { Option } = Select;
const { Step } = Steps;

const DELIVERY_METHODS = [
  { value: 'in_person', label: 'In Person' },
  { value: 'online', label: 'Online' },
  { value: 'blended', label: 'Blended' },
  { value: 'on_the_job', label: 'On-the-Job' },
  { value: 'self_paced', label: 'Self-Paced' },
];

const RunSessionWizard = ({ visible, propertyId, onClose }) => {
  const [step, setStep] = useState(0);
  const [form1] = Form.useForm();
  const [form2] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const [assets, setAssets] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [peopleSearch, setPeopleSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [competencyMap, setCompetencyMap] = useState({});
  // {} rather than null: it is a partly-filled form from the first render, and
  // the step-restore effect below reads into it before any step has been
  // completed.
  const [sessionData, setSessionData] = useState({});
  // Set when the backend confirms the save. Drives the QR step — kept
  // separate from sessionData (which is the in-progress form state) so the
  // wizard can show the post-save URL without re-deriving it from input.
  const [savedSession, setSavedSession] = useState(null);
  const [outsideForm] = Form.useForm();

  useEffect(() => {
    if (visible && propertyId) {
      fetchAssets();
      fetchUsers();
      resetWizard();
    }
  }, [visible, propertyId]);

  // Going BACK must show what was entered, not an empty box.
  //
  // The step's <Form> was unmounted while you were elsewhere, so it remounts
  // blank unless it is refilled from what was banked. Without this, stepping
  // back and forward again would re-bank the empty field and wipe the value —
  // trading one way of losing the venue for another.
  useEffect(() => {
    if (step === 0) {
      form1.setFieldsValue(sessionData);
    }
    if (step === 1) {
      form2.setFieldsValue({ location_name: sessionData.location_name });
    }
    // sessionData is deliberately not a dependency: this restores on ARRIVAL at
    // a step, and re-running it on every keystroke would fight the typist.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const resetWizard = () => {
    setStep(0);
    setSessionData({});
    form1.resetFields();
    form2.resetFields();
    setSelectedAsset(null);
    setAttendees([]);
    setPeopleSearch('');
    setSearchResults([]);
    setCompetencyMap({});
    setSessionData(null);
    setSavedSession(null);
    outsideForm.resetFields();
  };

  const fetchAssets = async () => {
    const res = await educationAssetService.getAll(propertyId);
    if (res) setAssets(res.education_assets || []);
  };

  const fetchUsers = async () => {
    try {
      const res = await userService.getUserForProperty(propertyId);
      if (res && res.code === '0000') setUsers(res.users || []);
    } catch (e) { console.error('Failed to fetch users:', e); }
  };

  const handleAssetChange = (assetId) => {
    const asset = assets.find(a => a.id === assetId);
    setSelectedAsset(asset);
  };

  const searchTimeout = React.useRef(null);
  const handleSearchPeople = (value) => {
    setPeopleSearch(value);
    if (!value || value.length < 2) { setSearchResults([]); return; }
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await trainingService.searchPeople(propertyId, value);
        if (res) setSearchResults(res.people || []);
      } catch (e) {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 350);
  };

  const addAttendee = (person) => {
    if (attendees.find(a => a.id === person.id && a.source_type === person.source_type)) return;
    setAttendees(prev => [...prev, person]);
    setCompetencyMap(prev => ({ ...prev, [person.id + '_' + person.source_type]: 'competent' }));
    setPeopleSearch('');
    setSearchResults([]);
  };

  const removeAttendee = (person) => {
    setAttendees(prev => prev.filter(a => !(a.id === person.id && a.source_type === person.source_type)));
  };

  const handleAddAllStaff = () => {
    const existingIds = new Set(attendees.filter(a => a.source_type === 'user').map(a => a.id));
    const newStaff = users.filter(u => !existingIds.has(u.id)).map(u => ({
      id: u.id,
      source_type: 'user',
      name: u.name,
      email: u.email,
      phone: u.mobile_phone,
      person_type: 'staff',
    }));
    if (newStaff.length === 0) {
      message.info('All staff already added');
      return;
    }
    setAttendees(prev => [...prev, ...newStaff]);
    const newCompetency = {};
    newStaff.forEach(s => { newCompetency[s.id + '_user'] = 'competent'; });
    setCompetencyMap(prev => ({ ...prev, ...newCompetency }));
    message.success(`Added ${newStaff.length} staff members (marked competent by default)`);
  };

  const handleAddOutsideTrainee = async () => {
    try {
      const values = await outsideForm.validateFields();
      const newPerson = {
        id: `outside_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        source_type: 'outside',
        name: `${values.first_name} ${values.last_name}`,
        email: values.email,
        phone: values.mobile_phone || '',
        person_type: 'external',
        first_name: values.first_name,
        last_name: values.last_name,
      };
      setAttendees(prev => [...prev, newPerson]);
      setCompetencyMap(prev => ({ ...prev, [newPerson.id + '_outside']: 'competent' }));
      outsideForm.resetFields();
      message.success('Outside trainee added successfully');
    } catch (e) { /* validation failed — inline errors shown */ }
  };

  // EACH STEP BANKS ITS OWN ANSWERS, and merges rather than replaces.
  //
  // Only the current step is rendered, so a step's <Form> is unmounted the
  // moment you leave it — and antd's getFieldsValue() returns REGISTERED fields
  // only, so reading a form you have navigated away from yields nothing. That
  // is exactly how the venue was being lost: step 2 validated the field and
  // threw the value away, and the save then read it back off an unmounted form.
  //
  // Replacing rather than merging was the second half of it: going Back to
  // step 1 and forward again used to overwrite the whole object, dropping the
  // venue that had been entered after it.
  const handleStep1Next = async () => {
    try {
      const values = await form1.validateFields();
      setSessionData(prev => ({ ...prev, ...values }));
      setStep(1);
    } catch (e) { console.error('Step 1 validation failed:', e); }
  };

  const handleStep2Next = async () => {
    try {
      const values = await form2.validateFields();
      setSessionData(prev => ({ ...prev, ...values }));
      setStep(2);
    } catch (e) { console.error('Step 2 validation failed:', e); }
  };

  /**
   * Save the session.
   *
   * Two routes end up here, and they are different jobs:
   *
   *   - "Create and activate" (from the Location step): the session is about to
   *     run. Nobody has attended yet, so it saves with an empty roster, opens
   *     for registration and goes straight to the QR code — which is the whole
   *     point, because the attendees are standing there waiting to scan it.
   *
   *   - "Save session" (from the Record Attendance step): the training already
   *     happened and is being written up. The roster and the competency
   *     verdicts are part of the record and go in with it.
   *
   * Same endpoint, same payload shape; what differs is whether trainees ride
   * along and which status the session lands in.
   */
  const handleFinish = async ({ status = 'active', withAttendees = true } = {}) => {
    setSaving(true);
    try {
      const payload = {
        status,
        // Carries location_name, banked by handleStep2Next. Reading form2 here
        // returned nothing: by this point its fields are unmounted.
        ...sessionData,
        property_id: propertyId,
        session_date: sessionData.session_date ? sessionData.session_date.format('YYYY-MM-DD') : null,
        start_time: sessionData.start_time ? sessionData.start_time.format('HH:mm:ss') : null,
        finish_time: sessionData.finish_time ? sessionData.finish_time.format('HH:mm:ss') : null,
        training_name: selectedAsset ? selectedAsset.name : null,
        // null = "inherit the asset's default", which is a different thing from
        // "no assessment" and has to be sent explicitly to be applied.
        assessment_form_id: sessionData.assessment_form_id || null,
        trainees: !withAttendees ? [] : attendees.map(a => ({
          trainee_type: a.source_type === 'user' ? 'property_user' : 'outside_trainee',
          user_id: a.source_type === 'user' ? a.id : null,
          first_name: a.first_name || (a.source_type !== 'user' ? (a.name || '').split(' ')[0] : null),
          last_name: a.last_name || (a.source_type !== 'user' ? ((a.name || '').split(' ').slice(1).join(' ') || '-') : null),
          email: a.email,
          mobile_phone: a.phone,
          competency_status: competencyMap[a.id + '_' + a.source_type] || 'competent',
        })),
      };

      const res = await educationSessionService.storeOrUpdate(payload);
      if (res && res.code === '0000') {
        if (status === 'draft') {
          // A draft has no QR to show — that is what activating is for — so
          // there is nothing to keep the wizard open for.
          message.success('Session saved as a draft. Activate it when it starts.');
          onClose(true);
          return;
        }
        message.success(withAttendees
          ? 'Training session saved successfully'
          : 'Session activated. Attendees can scan the code to register.');
        // Surface the QR step instead of closing immediately so the
        // trainer can display/print the code for attendees to scan.
        // education_session is what the controller returns; falling back
        // to null keeps the modal alive (the next step renders an error
        // hint) instead of crashing on a missing field.
        setSavedSession(res.education_session || null);
        setStep(4);
      } else {
        message.error(res?.message || 'Failed to save session');
      }
    } catch (e) {
      message.error('An error occurred while saving');
    } finally {
      setSaving(false);
    }
  };

  const steps = ['Session Details', 'Location', 'Add Attendees', 'Record Attendance', 'Share QR Code'];

  const renderStep1 = () => (
    <Form form={form1} layout="vertical">
      <Form.Item name="education_asset_id" label="Training Asset" rules={[{ required: true, message: 'Select a training asset' }]}>
        <Select placeholder="Select training asset" onChange={handleAssetChange} showSearch optionFilterProp="children">
          {assets.map(a => <Option key={a.id} value={a.id}>{a.name}</Option>)}
        </Select>
      </Form.Item>

      {selectedAsset && (
        <Card size="small" style={{ marginBottom: 16, background: '#f6fff9', border: '1px solid #b7eb8f' }}>
          <strong>{selectedAsset.name}</strong>
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
            {selectedAsset.description || 'No description'}
          </div>
          <div style={{ marginTop: 6 }}>
            <Tag color="blue">{selectedAsset.type}</Tag>
            {selectedAsset.validity_months && <Tag>{selectedAsset.validity_months} month validity</Tag>}
          </div>
        </Card>
      )}

      <Form.Item
        name="assessment_form_id"
        label="Assessment for this session"
        extra={
          selectedAsset && selectedAsset.assessment_form_id
            ? `Leave empty to use the training asset's default: ${selectedAsset.assessment_form_name || 'assessment form'}.`
            : 'Optional. Attendees are asked to complete this form after the session.'
        }
      >
        <AssessmentFormSelect
          propertyId={propertyId}
          placeholder={
            selectedAsset && selectedAsset.assessment_form_id
              ? `Default: ${selectedAsset.assessment_form_name || 'asset assessment'}`
              : 'No assessment'
          }
        />
      </Form.Item>

      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="trainer_id" label="Trainer" rules={[{ required: true, message: 'Select a trainer' }]}>
            <Select placeholder="Select trainer" showSearch optionFilterProp="children">
              {users.map(u => <Option key={u.id} value={u.id}>{u.name}</Option>)}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="session_date" label="Session Date" rules={[{ required: true, message: 'Select a date' }]}>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="delivery_method" label="Delivery Method">
        <Select placeholder="Select delivery method">
          {DELIVERY_METHODS.map(m => <Option key={m.value} value={m.value}>{m.label}</Option>)}
        </Select>
      </Form.Item>

      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="start_time" label="Start Time" rules={[{ required: true, message: 'Select start time' }]}>
            <TimePicker format="HH:mm" style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="finish_time" label="End Time">
            <TimePicker format="HH:mm" style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );

  const renderStep2 = () => (
    <Form form={form2} layout="vertical">
      <Form.Item name="location_name" label="Location / Venue">
        <Input placeholder="e.g. Main office meeting room, Site A shed" />
      </Form.Item>
    </Form>
  );

  const renderStep3 = () => (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        <Input
          className="training-search-bar"
          placeholder="Search by name, email or phone..."
          value={peopleSearch}
          onChange={e => handleSearchPeople(e.target.value)}
          prefix={<UserOutlined />}
          style={{ flex: 1 }}
        />
        <Button
          icon={<UsergroupAddOutlined />}
          onClick={handleAddAllStaff}
          style={{ borderColor: '#0B2A1B', color: '#0B2A1B' }}
        >
          Add All Staff
        </Button>
      </div>
      {searchLoading && <div style={{ color: '#8c8c8c', marginBottom: 8 }}>Searching...</div>}
      {searchResults.length > 0 && (
        <Card size="small" style={{ marginBottom: 16, maxHeight: 200, overflowY: 'auto' }}>
          {searchResults.map(p => (
            <div
              key={p.id + p.source_type}
              style={{ padding: '6px 0', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <span>
                <strong>{p.name}</strong>
                <Tag style={{ marginLeft: 8 }}>{p.person_type}</Tag>
                <span style={{ color: '#8c8c8c', fontSize: 12 }}> {p.email}</span>
              </span>
              <Button
                size="small"
                icon={<PlusOutlined />}
                style={{ borderColor: '#0B2A1B', color: '#0B2A1B' }}
                onClick={() => addAttendee(p)}
              >
                Add
              </Button>
            </div>
          ))}
        </Card>
      )}

      {/* Add Outside Trainee */}
      <Card size="small" title="Add Outside Trainee" style={{ marginBottom: 16 }}>
        <Form form={outsideForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="first_name" label="First Name" rules={[{ required: true, whitespace: true, message: 'First name is required' }]} style={{ marginBottom: 8 }}>
                <Input placeholder="First name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="last_name" label="Last Name" rules={[{ required: true, whitespace: true, message: 'Last name is required' }]} style={{ marginBottom: 8 }}>
                <Input placeholder="Last name" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="email" label="Email" rules={[{ required: true, whitespace: true, message: 'Email is required' }, { type: 'email', message: 'Please enter a valid email' }]} style={{ marginBottom: 8 }}>
                <Input placeholder="email@example.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="mobile_phone" label="Mobile Phone" style={{ marginBottom: 8 }}>
                <Input placeholder="Mobile phone number" />
              </Form.Item>
            </Col>
          </Row>
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={handleAddOutsideTrainee}
            style={{ width: '100%' }}
          >
            Add Outside Trainee
          </Button>
        </Form>
      </Card>

      <Divider>Added Attendees ({attendees.length})</Divider>
      {attendees.length === 0 ? (
        <div style={{ color: '#8c8c8c', textAlign: 'center', padding: 20 }}>No attendees added yet</div>
      ) : (
        <List
          dataSource={attendees}
          renderItem={a => (
            <List.Item
              actions={[
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeAttendee(a)}
                />,
              ]}
            >
              <List.Item.Meta
                avatar={<Avatar>{(a.name || '?')[0].toUpperCase()}</Avatar>}
                title={a.name}
                description={<span><Tag>{a.person_type}</Tag>{a.email}</span>}
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  const renderStep4 = () => (
    <div>
      {attendees.length === 0 ? (
        <div style={{ color: '#8c8c8c', textAlign: 'center', padding: 20 }}>No attendees to record</div>
      ) : (
        <List
          dataSource={attendees}
          renderItem={a => {
            const key = a.id + '_' + a.source_type;
            return (
              <List.Item>
                <List.Item.Meta
                  avatar={<Avatar>{(a.name || '?')[0].toUpperCase()}</Avatar>}
                  title={a.name}
                  description={<Tag>{a.person_type}</Tag>}
                />
                <Select
                  value={competencyMap[key] || 'competent'}
                  onChange={v => setCompetencyMap(prev => ({ ...prev, [key]: v }))}
                  style={{ width: 180 }}
                >
                  <Option value="competent">
                    <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                    Competent
                  </Option>
                  <Option value="not_yet_competent">Not Yet Competent</Option>
                </Select>
              </List.Item>
            );
          }}
        />
      )}
    </div>
  );

  // Post-save step: shows the QR code attendees scan to self-register on
  // mobile. The url is whatever the backend returned in registration_url;
  // we never build it on the client so a future route change only needs
  // EducationSession::registrationUrl to be updated.
  const renderStep5 = () => {
    const url = savedSession?.registration_url;
    return (
      <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
        <div style={{ marginBottom: 12 }}>
          <CheckCircleOutlined style={{ fontSize: 36, color: '#52c41a' }} />
          <h3 style={{ marginTop: 8, marginBottom: 4 }}>Session saved</h3>
          <div style={{ color: '#8c8c8c' }}>
            Attendees can scan this code with the AirAgri app to register.
          </div>
        </div>

        {savedSession?.assessment?.has_assessment && (
          <Alert
            style={{ marginBottom: 16, textAlign: 'left', borderRadius: 8 }}
            type="info"
            showIcon
            message={<span>Scanning also opens <strong>{savedSession.assessment.form_name}</strong></span>}
            description={
              savedSession.assessment.scored
                ? `Answers are marked automatically — ${savedSession.assessment.pass_mark}% or above is a pass. You can still change anyone's competency afterwards.`
                : 'Answers are recorded as evidence. You confirm each attendee\'s competency afterwards.'
            }
          />
        )}


        {url ? (
          <>
            <div style={{ display: 'inline-block', padding: 16, background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8 }}>
              <QRCode value={url} size={220} />
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: '#8c8c8c', wordBreak: 'break-all' }}>
              {url}
            </div>
            <div style={{ marginTop: 8 }}>
              <Button
                size="small"
                onClick={() => {
                  if (navigator.clipboard) {
                    navigator.clipboard.writeText(url);
                    message.success('Link copied');
                  }
                }}
              >
                Copy link
              </Button>
            </div>

            {/* The trainer is standing in front of the room with this on the
                screen. Watching the roster fill is the confirmation that the
                code works — without it they have to close the wizard and go
                looking for the session to find out. */}
            <div style={{ marginTop: 20, maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}>
              <LiveAttendeeRoster
                sessionId={savedSession?.id}
                enabled={!!savedSession?.id}
                maxHeight={220}
              />
            </div>
          </>
        ) : (
          // Fallback path: backend save succeeded but registration_url
          // didn't make it back (commonest cause: FE_BASE_URL not set on
          // this env). The trainer can't get back to a prior wizard step
          // without re-saving, so we surface the session id verbatim so
          // support can finish manually, plus a copy button for it. The
          // QR feature itself is degraded but the session DID save.
          <div>
            <div style={{ color: '#cf1322', marginBottom: 12 }}>
              Session saved but no registration URL was returned by the
              server. Share the session id with support to enable the
              QR for this session.
            </div>
            {savedSession?.id && (
              <div style={{ background: '#fafafa', border: '1px solid #e8e8e8', borderRadius: 6, padding: 12, display: 'inline-block' }}>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>Session ID</div>
                <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>
                  {savedSession.id}
                </div>
                <Button
                  size="small"
                  style={{ marginTop: 8 }}
                  onClick={() => {
                    if (navigator.clipboard) {
                      navigator.clipboard.writeText(String(savedSession.id));
                      message.success('Session ID copied');
                    }
                  }}
                >
                  Copy ID
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const stepContent = [renderStep1, renderStep2, renderStep3, renderStep4, renderStep5];

  return (
    <Modal
      title="Run New Training Session"
      open={visible}
      onCancel={onClose}
      width={680}
      footer={
        step === 4 ? (
          // QR step is post-save and terminal: no Back (would re-open the
          // attendance form for a session that's already persisted) and
          // no Next. Just Done, which closes the modal and signals the
          // parent to refresh its session list.
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="primary"
              style={{ background: '#0B2A1B', borderColor: '#0B2A1B' }}
              onClick={() => onClose(true)}
            >
              Done
            </Button>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={step === 0 ? onClose : () => setStep(s => s - 1)}>
              {step === 0 ? 'Cancel' : 'Back'}
            </Button>
            <div style={{ display: 'flex', gap: 8 }}>
              {/* The Location step is the fork. Everything the session NEEDS has
                  been answered by this point — the two steps after it are for
                  writing up training that already happened. A trainer about to
                  run one takes the middle button and gets the QR code; the
                  attendees add themselves. */}
              {step === 1 && (
                <>
                  <Button
                    loading={saving}
                    onClick={() => handleFinish({ status: 'draft', withAttendees: false })}
                  >
                    Save as draft
                  </Button>
                  <Button
                    type="primary"
                    style={{ background: '#0B2A1B', borderColor: '#0B2A1B' }}
                    loading={saving}
                    onClick={async () => {
                      // Bank this step's field first: the venue lives on a form
                      // that is about to unmount, and skipping ahead without
                      // this is how it used to get lost.
                      try {
                        const values = await form2.validateFields();
                        setSessionData(prev => ({ ...prev, ...values }));
                        await handleFinish({ status: 'active', withAttendees: false });
                      } catch (e) { /* inline validation errors are shown */ }
                    }}
                  >
                    Create &amp; activate now
                  </Button>
                </>
              )}
              {step < 3 ? (
                <Button
                  // Once "Create and activate" exists, this is the
                  // record-it-afterwards route, so name it that rather than
                  // leaving two identically-labelled primary buttons.
                  type={step === 1 ? 'default' : 'primary'}
                  style={step === 1 ? undefined : { background: '#0B2A1B', borderColor: '#0B2A1B' }}
                  onClick={step === 0 ? handleStep1Next : step === 1 ? handleStep2Next : () => setStep(s => s + 1)}
                >
                  {step === 1 ? 'Add attendees instead' : 'Next'}
                </Button>
              ) : (
                <Button
                  type="primary"
                  style={{ background: '#0B2A1B', borderColor: '#0B2A1B' }}
                  loading={saving}
                  onClick={() => handleFinish({ status: 'active', withAttendees: true })}
                >
                  Save Session
                </Button>
              )}
            </div>
          </div>
        )
      }
      destroyOnClose
    >
      <Steps current={step} size="small" style={{ marginBottom: 24 }}>
        {steps.map((s, i) => <Step key={i} title={s} />)}
      </Steps>
      <div className="wizard-section">
        {stepContent[step]()}
      </div>
    </Modal>
  );
};

export default RunSessionWizard;
